import { create } from 'zustand'
import {
  fetchWeather,
  refreshWeather,
  type RequestOptions,
  type WeatherManualRefreshResponse,
  type WeatherManualRefreshStatus,
  type WeatherRateLimit,
  type WeatherState,
} from '../api/weather'

export const WEATHER_POLL_INTERVAL_MS = 20 * 60 * 1000
const MIN_POLL_INTERVAL_MS = 60 * 1000
const DEFAULT_FETCH_ERROR_MESSAGE = 'Unable to load weather data.'
const DEFAULT_REFRESH_ERROR_MESSAGE = 'Unable to refresh weather.'

type WeatherErrorState = {
  message: string
  status: number | null
  code: string | null
  occurredAt: number
}

type WeatherManualRefreshState = {
  status: WeatherManualRefreshStatus | null
  message: string | null
  errorCode: string | null
  nextAllowedAt: string | null
  occurredAt: number | null
}

type WeatherEntryState = {
  stationId: number
  data: WeatherState | null
  loading: boolean
  refreshing: boolean
  hasFetched: boolean
  error: WeatherErrorState | null
  manualRefresh: WeatherManualRefreshState
  pollTimer: ReturnType<typeof setTimeout> | null
  nextPollAt: number | null
}

type WeatherStoreState = {
  stations: Record<number, WeatherEntryState>
  fetch: (stationId: number, options?: RequestOptions) => Promise<WeatherState | null>
  manualRefresh: (stationId: number, options?: RequestOptions) => Promise<WeatherManualRefreshResponse | null>
  scheduleNextPoll: (stationId: number, delayMs?: number) => void
  clearPoll: (stationId: number) => void
  resetStation: (stationId: number) => void
  reset: () => void
}

const createManualRefreshState = (): WeatherManualRefreshState => ({
  status: null,
  message: null,
  errorCode: null,
  nextAllowedAt: null,
  occurredAt: null,
})

const createEntryState = (stationId: number): WeatherEntryState => ({
  stationId,
  data: null,
  loading: false,
  refreshing: false,
  hasFetched: false,
  error: null,
  manualRefresh: createManualRefreshState(),
  pollTimer: null,
  nextPollAt: null,
})

const isAbortError = (error: unknown): error is DOMException =>
  Boolean(error) && typeof error === 'object' && (error as { name?: string }).name === 'AbortError'

const extractErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null
  }

  const data = (error as { data?: unknown }).data
  if (data && typeof data === 'object') {
    const typed = data as { error_code?: unknown; code?: unknown }
    if (typeof typed.error_code === 'string' && typed.error_code) {
      return typed.error_code
    }
    if (typeof typed.code === 'string' && typed.code) {
      return typed.code
    }
  }

  return null
}

const extractRateLimits = (payload: unknown): Record<string, WeatherRateLimit> | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const { rate_limits: rateLimits } = payload as { rate_limits?: unknown }
  if (!rateLimits || typeof rateLimits !== 'object') {
    return null
  }

  return rateLimits as Record<string, WeatherRateLimit>
}

const extractWeatherState = (payload: unknown): WeatherState | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const { weather } = payload as { weather?: unknown }
  if (!weather || typeof weather !== 'object') {
    return null
  }

  return weather as WeatherState
}

const extractNextAllowedAt = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const { next_allowed_at: nextAllowedAt } = payload as { next_allowed_at?: unknown }
  return typeof nextAllowedAt === 'string' && nextAllowedAt ? nextAllowedAt : null
}

const extractManualStatus = (payload: unknown): WeatherManualRefreshStatus | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const { status } = payload as { status?: unknown }
  if (typeof status === 'string') {
    if (status === 'success' || status === 'cooldown' || status === 'error') {
      return status
    }
  }

  return null
}

const useWeatherStore = create<WeatherStoreState>((set, get) => ({
  stations: {},
  async fetch(stationId, options = {}) {
    if (!Number.isFinite(stationId) || stationId <= 0) {
      return null
    }

    set((state) => {
      const prev = state.stations[stationId] ?? createEntryState(stationId)
      return {
        stations: {
          ...state.stations,
          [stationId]: {
            ...prev,
            loading: true,
            error: null,
          },
        },
      }
    })

    try {
      const response = await fetchWeather(stationId, options)

      set((state) => {
        const prev = state.stations[stationId] ?? createEntryState(stationId)
        return {
          stations: {
            ...state.stations,
            [stationId]: {
              ...prev,
              data: response,
              loading: false,
              hasFetched: true,
              error: null,
            },
          },
        }
      })

      get().scheduleNextPoll(stationId)

      return response
    } catch (error) {
      if (isAbortError(error)) {
        set((state) => {
          const prev = state.stations[stationId] ?? createEntryState(stationId)
          return {
            stations: {
              ...state.stations,
              [stationId]: {
                ...prev,
                loading: false,
              },
            },
          }
        })
        return null
      }

      const message =
        error instanceof Error && error.message ? error.message : DEFAULT_FETCH_ERROR_MESSAGE
      const status = typeof (error as { status?: unknown }).status === 'number'
        ? ((error as { status?: number }).status as number)
        : null
      const code = extractErrorCode(error)
      const occurredAt = Date.now()

      set((state) => {
        const prev = state.stations[stationId] ?? createEntryState(stationId)
        return {
          stations: {
            ...state.stations,
            [stationId]: {
              ...prev,
              loading: false,
              error: {
                message,
                status,
                code,
                occurredAt,
              },
            },
          },
        }
      })

      get().scheduleNextPoll(stationId)

      return null
    }
  },
  async manualRefresh(stationId, options = {}) {
    if (!Number.isFinite(stationId) || stationId <= 0) {
      return null
    }

    set((state) => {
      const prev = state.stations[stationId] ?? createEntryState(stationId)
      return {
        stations: {
          ...state.stations,
          [stationId]: {
            ...prev,
            refreshing: true,
          },
        },
      }
    })

    try {
      const response = await refreshWeather(stationId, options)
      const occurredAt = Date.now()

      set((state) => {
        const prev = state.stations[stationId] ?? createEntryState(stationId)
        return {
          stations: {
            ...state.stations,
            [stationId]: {
              ...prev,
              data: response.weather,
              refreshing: false,
              hasFetched: true,
              error: null,
              manualRefresh: {
                status: response.status,
                message: response.message,
                errorCode: response.error_code,
                nextAllowedAt: response.next_allowed_at,
                occurredAt,
              },
              nextPollAt: prev.nextPollAt,
              pollTimer: prev.pollTimer,
            },
          },
        }
      })

      get().scheduleNextPoll(stationId)

      return response
    } catch (error) {
      if (isAbortError(error)) {
        set((state) => {
          const prev = state.stations[stationId] ?? createEntryState(stationId)
          return {
            stations: {
              ...state.stations,
              [stationId]: {
                ...prev,
                refreshing: false,
              },
            },
          }
        })
        return null
      }

      const payload = (error as { data?: unknown }).data
      const weather = extractWeatherState(payload)
      const rateLimits = extractRateLimits(payload)
      const manualStatus = extractManualStatus(payload) ?? 'error'
      const nextAllowedAt = extractNextAllowedAt(payload)
      const message =
        error instanceof Error && error.message ? error.message : DEFAULT_REFRESH_ERROR_MESSAGE
      const errorCode = extractErrorCode(error)
      const status = typeof (error as { status?: unknown }).status === 'number'
        ? ((error as { status?: number }).status as number)
        : null
      const occurredAt = Date.now()
      let shouldSchedule = false
      let scheduleDelay: number | undefined

      set((state) => {
        const prev = state.stations[stationId] ?? createEntryState(stationId)

        const resolvedData = (() => {
          if (weather) {
            return rateLimits ? { ...weather, rate_limits: rateLimits } : weather
          }

          if (!prev.data) {
            return null
          }

          if (rateLimits) {
            return { ...prev.data, rate_limits: rateLimits }
          }

          return prev.data
        })()

        if (resolvedData || prev.data) {
          shouldSchedule = true
        }

        if (nextAllowedAt) {
          const parsed = Date.parse(nextAllowedAt)
          if (!Number.isNaN(parsed)) {
            const diff = parsed - occurredAt
            if (diff > 0) {
              scheduleDelay = diff
            }
          }
        }

        const nextError =
          prev.data || resolvedData
            ? prev.error
            : {
                message,
                status,
                code: errorCode,
                occurredAt,
              }

        return {
          stations: {
            ...state.stations,
            [stationId]: {
              ...prev,
              data: resolvedData,
              refreshing: false,
              hasFetched: prev.hasFetched || Boolean(resolvedData),
              manualRefresh: {
                status: manualStatus,
                message,
                errorCode,
                nextAllowedAt,
                occurredAt,
              },
              error: nextError,
              pollTimer: prev.pollTimer,
              nextPollAt: prev.nextPollAt,
            },
          },
        }
      })

      if (shouldSchedule) {
        if (scheduleDelay !== undefined) {
          get().scheduleNextPoll(stationId, scheduleDelay)
        } else {
          get().scheduleNextPoll(stationId)
        }
      }

      return null
    }
  },
  scheduleNextPoll(stationId, delayMs = WEATHER_POLL_INTERVAL_MS) {
    if (!Number.isFinite(stationId) || stationId <= 0) {
      return
    }

    const delay =
      typeof delayMs === 'number' && Number.isFinite(delayMs) && delayMs > 0
        ? Math.max(delayMs, MIN_POLL_INTERVAL_MS)
        : WEATHER_POLL_INTERVAL_MS
    const state = get()
    const prev = state.stations[stationId] ?? createEntryState(stationId)

    if (prev.pollTimer) {
      clearTimeout(prev.pollTimer)
    }

    const nextAt = Date.now() + delay
    const timer = setTimeout(() => {
      void get().fetch(stationId).catch((pollError) => {
        if (!isAbortError(pollError) && (import.meta as any)?.env?.DEV) {
          console.error('[RPA] Weather poll failed', pollError)
        }
      })
    }, delay)

    set({
      stations: {
        ...state.stations,
        [stationId]: {
          ...prev,
          pollTimer: timer,
          nextPollAt: nextAt,
        },
      },
    })
  },
  clearPoll(stationId) {
    const state = get()
    const prev = state.stations[stationId]

    if (!prev) {
      return
    }

    if (prev.pollTimer) {
      clearTimeout(prev.pollTimer)
    }

    set({
      stations: {
        ...state.stations,
        [stationId]: {
          ...prev,
          pollTimer: null,
          nextPollAt: null,
        },
      },
    })
  },
  resetStation(stationId) {
    const state = get()
    const prev = state.stations[stationId]

    if (!prev) {
      return
    }

    if (prev.pollTimer) {
      clearTimeout(prev.pollTimer)
    }

    const next = { ...state.stations }
    delete next[stationId]

    set({ stations: next })
  },
  reset() {
    const state = get()

    Object.values(state.stations).forEach((entry) => {
      if (entry.pollTimer) {
        clearTimeout(entry.pollTimer)
      }
    })

    set({ stations: {} })
  },
}))

export default useWeatherStore
